// React
import { useLayoutEffect, useRef, type FC } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';

// Styles
import '../styles/EndGame.css';

// Components
import MissionHeader from '../components/MissionHeader';
import MainButton from '../components/MainButton';

// Contexts
import { useTimer } from '../contextes/useTimer';

// GSAP
import gsap from 'gsap';

// Data
import { contentData } from '../assets/data/contentData';

const EndGame: FC = () => {
    const navigate = useNavigate();
    // Récupère le numéro de la mission depuis les paramètres d'URL
    const { missionNumber } = useParams<{ missionNumber: string }>();
    // Récupère le titre de la mission depuis les données en utilisant le numéro de la mission
    const missionTitle = contentData.missions.find(
        (mission) => mission.id === Number(missionNumber),
    )?.title;
    // Créer une référence pour l'élément DOM pour une animation
    const el = useRef<HTMLDivElement>(null);
    // Récupère le timer et les fonctions du contexte Timer
    const { time, formatTime, resetTimer, initialTime, stopTimer } = useTimer();
    const location = useLocation();
    // Variable que stocke la valeur : l'utilisateur a gagné ou perdu
    const hasWon = (location.state as { hasWon?: boolean } | null)?.hasWon ?? true;

    // Animation au montage du composant
    useLayoutEffect(() => {
        if (!el.current) return;
        
        stopTimer();

        const ctx = gsap.context(() => {
            // 1. Split du H1 en lettres
            const h1 = document.querySelector('.endgame-content h1') as HTMLElement;
            if (h1) {
                const text = h1.innerText;
                h1.innerHTML = text
                    .split('')
                    .map((letter) => `<span class="letter">${letter}</span>`)
                    .join('');
            }

            // 2. Création de la timeline GSAP
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            // 3. Animation des lettres, du texte et du bouton
            tl.from('.letter', {
                opacity: 0,
                y: 20,
                duration: 0.1,
                stagger: 0.05,
            })
                .from('.endgame-text', {
                    opacity: 0,
                    scale: 0.8,
                    duration: 0.8,
                })
                .from('.endgame-button', {
                    opacity: 0,
                    scale: 0.8,
                    duration: 0.5,
                });
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <div
            className='endgame-page'
            ref={el}
        >
            <MissionHeader
                showBackButton={false}
                showSettingsButton={false}
                showCloseButton={false}
                logoVariant='default'
                closeFunction={() => navigate('/')}
                closeButtonVariant='tertiary'
                showTimer={false}
            />
            <div className='endgame-content'>
                <h1>{hasWon ? 'Félicitations' : 'Temps écoulé'}</h1>
                <p className='endgame-text'>
                    {hasWon
                        ? `Vous êtes arrivés à la fin du ${missionNumber !== '0' ? `Chapitre ${missionNumber}` : ''} `
                        : `Le temps est écoulé pour le ${missionNumber !== '0' ? `Chapitre ${missionNumber}` : ''}`}
                    <br />
                    <span>{missionTitle}</span>!
                </p>
                <p className='endgame-text'>
                    {hasWon ? (
                        <>
                            Temps de jeu :<span> {formatTime(initialTime - time)} min</span>
                        </>
                    ) : (
                        'Vous pourrez réessayer en relançant la mission.'
                    )}
                </p>
                {missionNumber === '0' && hasWon && (
                    <p className=''>
                        <br />
                        Commencez à jouer à votre première mission !
                    </p>
                )}
            </div>
            <MainButton
                text={"Retour à l'accueil"}
                backgroundColor='tertiary'
                onClick={() => {
                    resetTimer();
                    navigate('/');
                }}
            />
        </div>
    );
};

export default EndGame;
